// Opportunities domain types

// Region as returned by GET /api/v1/opportunities
export interface OpportunityRegion {
  code: string;
  name: string; // plain string (not multilingual)
}

// Type field as returned by GET /api/v1/opportunities
export interface OpportunityTypeField {
  value: string;
  label: Record<string, string>; // keyed by lang, e.g. { es: 'Co-inversión guiada', en: 'Guided co-investment' }
}

// Eligibility tag as returned by GET /api/v1/opportunities (enriched, one entry per selected tag)
export interface OpportunityEligibilityTag {
  value: string;
  label: Record<string, string>; // keyed by lang, e.g. { es: 'Mujeres', en: 'Women', pt: 'Mulheres' }
}

export const OpportunityStatus = {
  WAITING_APPROVAL: 'WAITING_APPROVAL',
  APPROVED: 'APPROVED',
} as const;

export type OpportunityStatus = (typeof OpportunityStatus)[keyof typeof OpportunityStatus];

// Core opportunity entity — matches GET /api/v1/opportunities response (user view)
export interface Opportunity {
  id: string;
  name: string;
  description: string | null;
  deadline: string | null; // ISO date string
  link: string | null;
  prize: string | null;
  status: OpportunityStatus;
  regions: OpportunityRegion[];
  industries: string[];
  type: OpportunityTypeField | null;
  eligibility: OpportunityEligibilityTag[];
}

// Admin view extends user view with audit fields
export interface OpportunityAdmin extends Opportunity {
  createdBy: string | null;
  source: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  active: boolean;
}

// POST /api/v1/opportunities — upserts by link
export interface UpsertOpportunityPayload {
  name: string;
  description?: string;
  link?: string;
  deadline?: string; // ISO 8601
  prize?: string;
  source?: string;
  regions?: string[]; // region or country codes, e.g. ['MX', 'Latam']
  industries?: string[];
  type?: string;
  eligibility?: string[]; // eligibility tag values, validated BE-side against the 29 valid tags
}

// Response from POST /api/v1/opportunities — returns the created/updated opportunity
export type UpsertOpportunityResponse = Opportunity;

// Pagination metadata from API response
export interface PaginatedMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

// Generic paginated response wrapper
export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginatedMeta;
}

// Query params for GET /api/v1/opportunities
export interface OpportunityQueryParams {
  page?: number;
  limit?: number;
  search?: string; // title + description keywords, handled BE-side
  status?: OpportunityStatus; // single value — BE accepts one at a time
  type?: string;
  regions?: string[];
  industries?: string[];
  eligibility?: string[]; // csv-joined BE-side, e.g. ?eligibility=WOMEN,PRE_SEED
  activeOnly?: boolean;
  sortBy?: 'deadline' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}
